import React, { useState, useEffect } from "react";
import styles from "./Home.module.css";
import heroDiamond from "../../assets/diamonds/diamond_hero.png";
import centerpieceDiamond from "../../assets/diamonds/diamond_centerpiece.png";
import logoIGI from "../../assets/images/logo_IGI.png";
import logoJBT from "../../assets/images/logo_JBT.png";
import logoVDB from "../../assets/images/logo_VDB.png";
import oval from "../../assets/diamonds/diamond_oval.png";
import round from "../../assets/diamonds/diamond_round.png";
import princess from "../../assets/diamonds/diamond_princess.png";
import emerald from "../../assets/diamonds/diamond_emerald.png";
import pear from "../../assets/diamonds/diamond_pear.png";
import marquise from "../../assets/diamonds/diamond_marquise.png";
import heart from "../../assets/diamonds/diamond_heart.png";
import Brilliance from "../../assets/svgs/Brilliance.svg";
import Durability from "../../assets/svgs/Durability.svg";
import Precision from "../../assets/svgs/Precision.svg";
import Certified from "../../assets/svgs/Certified.svg";
import EcoConscious from "../../assets/svgs/Eco_Conscious.svg";
import Luxury from "../../assets/svgs/Luxury.svg";
import TestimonialSection from "../../components/TestimonialSection/TestimonialSection";
import FaqSection from "../../components/FaqSection/FaqSection";
import ContactPromptSection from "../../components/ContactPromptSection/ContactPromptSection";
import CertificationsSection from "../../components/CertificationsSection/CertificationsSection";
import { testimonials } from "../../constants/constants";
import { FAQ_DATA } from "../../constants/constants";
import {
  FiDroplet,
  FiCpu,
  FiFeather,
  FiSmile,
  FiShield,
  FiAward,
  FiTrendingUp,
  FiLeaf,
  FiCheckCircle,
  FiEye,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// Typewriter hook with blinking cursor, no erase, sequential typing
function useTypewriter(text, speed = 110, start = true, pause = 1200) {
  const [displayed, setDisplayed] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  useEffect(() => {
    if (!start) {
      setDisplayed("");
      return;
    }
    let i = 0;
    let interval;
    let restartTimeout;
    function type() {
      setDisplayed("");
      i = 0;
      interval = setInterval(() => {
        setDisplayed(text.slice(0, i + 1));
        i++;
        if (i === text.length) {
          clearInterval(interval);
          restartTimeout = setTimeout(type, pause);
        }
      }, speed);
    }
    type();
    return () => {
      clearInterval(interval);
      clearTimeout(restartTimeout);
    };
  }, [text, speed, start, pause]);
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 500);
    return () => clearInterval(cursorInterval);
  }, []);
  const isComplete = displayed === text;
  return displayed + (!isComplete && showCursor ? "_" : "");
}

const Home = () => {
  const [centerIndex, setCenterIndex] = useState(0);
  const testimonialsCount = testimonials.length;
  const [dragStartX, setDragStartX] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [faqOpenIndex, setFaqOpenIndex] = useState(null);
  const navigate = useNavigate();
  const [innovationKey, setInnovationKey] = useState(0);
  const [brillianceKey, setBrillianceKey] = useState(0);
  const [innovationDone, setInnovationDone] = useState(false);
  const [brillianceDone, setBrillianceDone] = useState(false);
  // Animation phase: 'innovation', 'brilliance', 'pause'
  const [phase, setPhase] = useState("innovation");
  const [innovationText, setInnovationText] = useState("");
  const [brillianceText, setBrillianceText] = useState("");
  const [showCursor, setShowCursor] = useState(true);

  const handleFaqToggle = (idx) => {
    setFaqOpenIndex(faqOpenIndex === idx ? null : idx);
  };

  // Typewriter for Innovation
  useEffect(() => {
    if (phase !== "innovation") return;
    setInnovationText("");
    setBrillianceText("");
    let i = 0;
    const interval = setInterval(() => {
      setInnovationText("Innovation".slice(0, i + 1));
      i++;
      if (i === "Innovation".length) {
        clearInterval(interval);
        setTimeout(() => setPhase("brilliance"), 400);
      }
    }, 110);
    return () => clearInterval(interval);
  }, [phase]);

  // Typewriter for Brilliance
  useEffect(() => {
    if (phase !== "brilliance") return;
    setBrillianceText("");
    let i = 0;
    const interval = setInterval(() => {
      setBrillianceText("Brilliance".slice(0, i + 1));
      i++;
      if (i === "Brilliance".length) {
        clearInterval(interval);
        setTimeout(() => setPhase("pause"), 2000);
      }
    }, 110);
    return () => clearInterval(interval);
  }, [phase]);

  // Pause before restarting
  useEffect(() => {
    if (phase !== "pause") return;
    const timeout = setTimeout(() => setPhase("innovation"), 400);
    return () => clearTimeout(timeout);
  }, [phase]);

  // Blinking cursor
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 500);
    return () => clearInterval(cursorInterval);
  }, []);

  const innovationDisplay =
    phase === "innovation"
      ? innovationText +
        (innovationText.length < "Innovation".length && showCursor ? "_" : "")
      : "Innovation";
  const brillianceDisplay =
    phase === "brilliance"
      ? brillianceText +
        (brillianceText.length < "Brilliance".length && showCursor ? "_" : "")
      : phase === "pause" || phase === "innovation"
      ? ""
      : "Brilliance";

  // Animation variants for hero section
  const heroVariants = {
    hidden: { opacity: 0, y: -60 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.2 + i * 0.18,
        duration: 0.7,
        type: "spring",
        stiffness: 60,
      },
    }),
  };
  const diamondVariants = {
    hidden: { opacity: 0, scale: 1.2 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { delay: 0.7, duration: 0.8, type: "spring", stiffness: 60 },
    },
  };
  const circleVariants = {
    hidden: { opacity: 0, scale: 1.2 },
    visible: (i = 0) => ({
      opacity: 0.8,
      scale: 1,
      transition: { delay: 0.7 + i * 0.12, duration: 0.7 },
    }),
  };

  return (
    <>
      <title>
        Green Rock Creations - Certified Wholesale Lab Grown Diamonds | Diamond
        Retailers USA, Europe, Hong Kong, India
      </title>
      <meta
        name="description"
        content="Green Rock Creations is a global leader in lab grown diamonds, offering wholesale diamonds and retail solutions for jewelers in the USA, Europe, Hong Kong, and India. Ethically sourced, certified, and sustainable diamonds for retailers and bulk buyers."
      />
      <meta
        name="keywords"
        content="diamond, diamonds, wholesale diamonds, lab grown diamonds, diamond retailers, buy diamonds, diamond supplier, diamond manufacturer, USA, Europe, Hong Kong, India, ethical diamonds, sustainable diamonds, certified diamonds, bulk diamonds, jewelry, diamond sale"
      />
      <link rel="canonical" href="https://www.greenrockcreations.com/" />
      <meta
        property="og:title"
        content="Wholesale Lab Grown Diamonds | Diamond Retailers USA, Europe, Hong Kong, India"
      />
      <meta
        property="og:description"
        content="Green Rock Creations is a global leader in lab grown diamonds, offering wholesale and retail diamond solutions for jewelers and bulk buyers in the USA, Europe, Hong Kong, and India."
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.greenrockcreations.com/" />
      <meta
        property="og:image"
        content="https://www.greenrockcreations.com/og-image.jpg"
      />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Green Rock Creations" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="Certified Wholesale Lab Grown Diamonds | Diamond Retailers USA, Europe, Hong Kong, India"
      />
      <meta
        name="twitter:description"
        content="Green Rock Creations is a global leader in lab grown diamonds, offering wholesale and retail diamond solutions for jewelers and bulk buyers in the USA, Europe, Hong Kong, and India."
      />
      <meta
        name="twitter:image"
        content="https://www.greenrockcreations.com/og-image.jpg"
      />
      <meta name="geo.region" content="US;EU;HK;IN" />
      <meta
        name="geo.placename"
        content="United States, Europe, Hong Kong, India"
      />
      <meta name="geo.position" content="37.0902;-95.7129" />
      <meta name="ICBM" content="37.0902, -95.7129" />
      <div className={styles.mainContainer}>
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroTextBlock}>
            <motion.h1
              className={styles.heroTitle}
              initial="hidden"
              animate="visible"
              variants={heroVariants}
              custom={0}
            >
              <span>Where </span>
              <span className={styles.heroTitleHighlight}>
                {innovationDisplay}
              </span>
              <br />
              <span style={{ display: "inline-block", minHeight: "1em" }}>
                <AnimatePresence mode="wait">
                  {phase !== "innovation" ? (
                    <motion.span
                      key="meets-brilliance"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.5 }}
                      style={{ display: "inline-block" }}
                    >
                      Meets{" "}
                      <span className={styles.heroTitleHighlight2}>
                        {brillianceDisplay}
                      </span>
                    </motion.span>
                  ) : (
                    <span style={{ visibility: "hidden" }}>
                      Meets{" "}
                      <span className={styles.heroTitleHighlight2}>
                        Brilliance
                      </span>
                    </span>
                  )}
                </AnimatePresence>
              </span>
            </motion.h1>
            <motion.p
              className={styles.heroSubtitle}
              initial="hidden"
              animate="visible"
              variants={heroVariants}
              custom={1}
            >
              Explore our diverse stone collection, crafted
              <br />
              for everyone using sustainable technology &<br />
              precision manufacturing.
            </motion.p>
            <motion.button
              className={styles.ctaButton}
              initial="hidden"
              animate="visible"
              variants={heroVariants}
              custom={2}
              onClick={() => navigate("/certified-diamonds")}
            >
              EXPLORE COLLECTION
            </motion.button>
          </div>
          <motion.div
            className={styles.heroDiamondBlock}
            initial="hidden"
            animate="visible"
            variants={diamondVariants}
          >
            {/* three blurred circles */}
            <motion.div className={styles.circleOuter} />
            <motion.div className={styles.circleMiddle} />
            <motion.div className={styles.circleInner} />
            <motion.img
              src={heroDiamond}
              alt="Diamond"
              className={styles.diamondImg}
              animate={{ y: [0, -8, 0, 8, 0] }}
              transition={{
                duration: 3.2,
                repeat: Infinity,
                repeatType: "loop",
                ease: "linear",
              }}
              style={{ willChange: "transform" }}
            />
          </motion.div>
        </section>

        {/* Diamond Section - Pixel Perfect */}
        <section className={styles.diamondShowcaseSection}>
          <div className={styles.diamondShowcaseSectionCircle1} />
          <div className={styles.diamondShowcaseSectionCircle2} />
          <div className={styles.diamondShowcaseSectionCircle3} />
          <div className={styles.diamondShowcaseSectionCircle4} />
          <div className={styles.diamondShowcaseSectionCircle5} />
          <div className={styles.diamondShowcaseSectionCircle6} />
          <div className={styles.diamondShowcaseCard}>
            {[
              {
                src: round,
                alt: "Round Diamond",
                className: styles.roundDiamondShowcaseStack,
                name: "Round",
              },
              {
                src: princess,
                alt: "Princess Diamond",
                className: styles.princessDiamondShowcaseStack,
                name: "Princess",
              },
              {
                src: oval,
                alt: "Oval Diamond",
                className: styles.ovalDiamondShowcaseStack,
                name: "Oval",
              },
              // Placement group for Pear and Marquise
              { placement: true },
              {
                src: emerald,
                alt: "Emerald Diamond",
                className: styles.emeraldDiamondShowcaseStack,
                name: "Emerald",
              },
              {
                src: heart,
                alt: "Heart Diamond",
                className: styles.heartDiamondShowcaseStack,
                name: "Heart",
              },
            ].map((item, idx) => {
              if (item.placement) {
                // Special placement for Pear and Marquise
                return (
                  <div
                    className={styles.diamondShowcaseCardPlacement}
                    key="placement"
                  >
                    <motion.div
                      className={`${styles.diamondStack} ${styles.pearDiamondShowcaseStack}`}
                      initial={{ opacity: 0, y: -60 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.22, delay: 0.22 * 3 }}
                      style={{ willChange: "transform, opacity" }}
                    >
                      <img src={pear} alt="Pear Diamond" />
                      <div className={styles.diamondName}>Pear</div>
                    </motion.div>
                    <motion.div
                      className={`${styles.diamondStack} ${styles.merquiseDiamondShowcaseStack}`}
                      initial={{ opacity: 0, y: -60 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.22, delay: 0.22 * 4 }}
                      style={{ willChange: "transform, opacity" }}
                    >
                      <img src={marquise} alt="Marquise Diamond" />
                      <div className={styles.diamondName}>Marquise</div>
                    </motion.div>
                  </div>
                );
              }
              // For all other cards
              return (
                <motion.div
                  key={item.name}
                  className={`${styles.diamondStack} ${item.className}`}
                  initial={{ opacity: 0, y: -60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.22, delay: 0.22 * idx }}
                  style={{ willChange: "transform, opacity" }}
                >
                  <img src={item.src} alt={item.alt} />
                  <div className={styles.diamondName}>{item.name}</div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* About Section */}
        <section className={"aboutSection container"}>
          <motion.h2
            className={styles.aboutTitle}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={heroVariants}
            custom={0}
          >
            About Us
          </motion.h2>
          <motion.p
            className={styles.aboutText}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={heroVariants}
            custom={1}
          >
            At Green Rock Creations Corp, we redefine luxury through innovation,
            crafting ethically grown diamonds that embody elegance,
            sustainability, and cutting-edge technology. As a regional leader in
            lab grown diamond manufacturing, we specialize in high-quality
            diamonds, exquisite fine jewellery, and advanced diamond materials
            that serve both traditional and high-tech markets.
            <span className={styles.aboutTextFaded}>
              {" "}
              Our passion lies in creating exquisite diamond
            </span>{" "}
            pieces that capture life's most meaningful moments.
          </motion.p>
        </section>

        {/* Purity Section */}
        <section className={styles.puritySection}>
          <div className={styles.purityContentWrapper}>
            <div className={styles.purityContainer}>
              <h2 className={styles.purityImageHeading}>
                Diamond{"'s "}
                Perfection,{" "}
                <span className={styles.purityImageHeadingHighlight2}>
                  Nature's Brilliance
                </span>
              </h2>
            </div>
            <motion.div
              className={styles.purityTextBlock}
              key="purityTextBlock"
              initial={{ opacity: 0, x: 120 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.8,
                type: "spring",
                stiffness: 50,
                delay: 0.4,
              }}
            >
              <FiTrendingUp className={styles.standForIcon} />
              <h2 className={styles.purityTitle}>Wholesale Advantage</h2>
              <p className={styles.purityDesc}>
                Unlock exclusive pricing, bulk benefits, and dedicated
                partnership opportunities designed for retailers and industry
                professionals seeking premium lab-grown diamonds at scale.
              </p>
            </motion.div>
          </div>
          <motion.div
            className={styles.purityFeaturesRow}
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: 0.8,
              type: "spring",
              stiffness: 50,
              delay: 0.4,
            }}
          >
            <motion.div
              className={styles.purityFeatureCard}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={heroVariants}
              custom={0}
            >
              <FiFeather className={styles.standForIcon} />
              <h3 className={styles.featureTitle}>Ethically Grown</h3>
              <p className={styles.featureDesc}>
                Our diamonds are cultivated in state-of-the-art labs, ensuring
                ethical practices and a minimal environmental footprint.
              </p>
            </motion.div>
            <motion.div
              className={styles.purityFeatureCard}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={heroVariants}
              custom={1}
            >
              <FiCheckCircle className={styles.standForIcon} />
              <h3 className={styles.featureTitle}>Industry Trusted</h3>
              <p className={styles.featureDesc}>
                Recognized and trusted by industry leaders for our commitment to
                quality, transparency, and reliability.
              </p>
            </motion.div>
            <motion.div
              className={styles.purityFeatureCard}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={heroVariants}
              custom={2}
            >
              <FiEye className={styles.standForIcon} />
              <h3 className={styles.featureTitle}>Visually Flawless</h3>
              <p className={styles.featureDesc}>
                Each stone is meticulously crafted to deliver exceptional
                clarity and brilliance, rivaling the finest natural diamonds.
              </p>
            </motion.div>
          </motion.div>
        </section>

        {/* Why Choose Us Section */}
        <section className={styles.WhyChooseUsSection}>
          <motion.h2
            className={styles.WhyChooseUsHeading}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={heroVariants}
            custom={0}
          >
            Why <span className={styles.WhyChooseUsGold}>Choose</span> Us
          </motion.h2>
          <motion.p
            className={styles.WhyChooseUsSubtitle}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={heroVariants}
            custom={1}
          >
            Because brilliance is our baseline, ethics are our edge, and
            excellence is our promise. We're not just selling lab-grown diamonds
            — we're delivering a new standard of purity, precision, and
            partnership.
          </motion.p>
          <div className={styles.WhyChooseUsGrid}>
            <div className={styles.WhyChooseUsCol}>
              <motion.div
                className={styles.WhyChooseUsCard}
                initial={{ opacity: 0, x: -80 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: 0.1 }}
              >
                <img
                  src={Durability}
                  alt="Wide Selection Icon"
                  className={styles.WhyChooseUsIcon}
                />
                <h3 className={styles.WhyChooseUsTitle}>
                  Unmatched Hardness & Durability
                </h3>
                <p className={styles.WhyChooseUsDesc}>
                  Engineered for resilience, our diamonds offer the same
                  superior hardness as mined stones, ensuring long-lasting
                  beauty and reliability for your customers' most cherished
                  pieces.
                </p>
              </motion.div>
              <motion.div
                className={styles.WhyChooseUsCard}
                initial={{ opacity: 0, x: -80 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: 0.1 }}
              >
                <img
                  src={Brilliance}
                  alt="Transparent Pricing Icon"
                  className={styles.WhyChooseUsIcon}
                />
                <h3 className={styles.WhyChooseUsTitle}>
                  Exceptional Brilliance & Fire
                </h3>
                <p className={styles.WhyChooseUsDesc}>
                  Our lab-grown diamonds are precision-cut to maximize light
                  reflection, delivering unmatched sparkle and fire that rivals
                  the finest natural stones—perfect for retailers seeking the
                  highest visual impact.
                </p>
              </motion.div>
            </div>
            <motion.div
              className={styles.WhyChooseUsDiamondWrapper}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: 0.1, type: "spring" }}
            >
              <img
                src={centerpieceDiamond}
                alt="Diamond Centerpiece"
                className={styles.WhyChooseUsDiamondImg}
              />
            </motion.div>
            <div className={styles.WhyChooseUsCol}>
              <motion.div
                className={styles.WhyChooseUsCard}
                initial={{ opacity: 0, x: 80 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: 0.1 }}
              >
                <img
                  src={Certified}
                  alt="Financing Icon"
                  className={styles.WhyChooseUsIcon}
                />
                <h3 className={styles.WhyChooseUsTitle}>
                  Certified Lab-Grown Purity
                </h3>
                <p className={styles.WhyChooseUsDesc}>
                  Each diamond is independently certified for its lab-grown
                  origin and purity, providing your business and clients with
                  full confidence in ethical sourcing and authenticity.
                </p>
              </motion.div>
              <motion.div
                className={styles.WhyChooseUsCard}
                initial={{ opacity: 0, x: 80 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: 0.1 }}
              >
                <img
                  src={EcoConscious}
                  alt="Customer Service Icon"
                  className={styles.WhyChooseUsIcon}
                />
                <h3 className={styles.WhyChooseUsTitle}>
                  Eco-Conscious & Conflict-Free
                </h3>
                <p className={styles.WhyChooseUsDesc}>
                  Our diamonds are produced with minimal environmental impact
                  and zero conflict, supporting your commitment to
                  sustainability and responsible sourcing in the jewelry
                  industry.
                </p>
              </motion.div>
            </div>
          </div>
          {/* New Feature Card centered below the diamond */}
          <motion.div
            className={styles.WhyChooseUsCardCenterWrapper}
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <div className={styles.WhyChooseUsCard}>
              <img
                src={Precision}
                alt="New Feature Icon"
                className={styles.WhyChooseUsIcon}
              />
              <h3 className={styles.WhyChooseUsTitle}>
                Precision-Cut Symmetry
              </h3>
              <p className={styles.WhyChooseUsDesc}>
                Every diamond is crafted with exacting symmetry and proportions,
                guaranteeing consistent quality and brilliance—ideal for
                jewelers who demand perfection in every stone.
              </p>
            </div>
          </motion.div>
        </section>

        {/* Step Hero Section */}
        <section className={styles.stepHeroSection}>
          <motion.div
            className={styles.stepHeroContent}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={heroVariants}
            custom={0}
          >
            <div className={styles.stepHeroLeft}>
              <h1 className={styles.stepHeroTitle}>
                We're shaping the future of{" "}
                <span className={styles.headingTitleHighlight}>
                  fine jewelry
                </span>{" "}
                —{" "}
                <span className={styles.headingTitleHighlight}>
                  one flawless diamond
                </span>{" "}
                at a time.
              </h1>
            </div>
          </motion.div>
          <motion.div
            className={styles.stepHeroImageWrapper}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={diamondVariants}
          >
            <img
              src={require("../../assets/other/9.png")}
              alt="Step"
              className={styles.stepHeroImage}
            />
          </motion.div>
        </section>

        {/* What We Stand For Section */}
        <section className={"standForSection container"}>
          <div className={styles.standForContent}>
            <div className={styles.standForLeft}>
              <motion.h2
                className={styles.standForHeading}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={heroVariants}
                custom={0}
              >
                What We <span className={styles.standForGold}>Stand</span> For
              </motion.h2>
              <div className={styles.standForSubtitle}>
                At the heart of our business lies purpose, precision, and
                progress. We don't just sell diamonds—we stand for a brighter
                future.
              </div>
              <div className={styles.standForListRow}>
                <ul
                  className={`${styles.standForList} ${styles.standForListColLeft}`}
                >
                  <motion.li
                    initial={{ opacity: 0, y: -40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.45, delay: 0 }}
                  >
                    <FiDroplet className={styles.standForIcon} />
                    <span className={styles.standForListText}>
                      Purity with Purpose
                    </span>
                  </motion.li>
                  <motion.li
                    initial={{ opacity: 0, y: -40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.45, delay: 0 }}
                  >
                    <FiFeather className={styles.standForIcon} />
                    <span className={styles.standForListText}>
                      Sustainability at the Core
                    </span>
                  </motion.li>
                  <motion.li
                    initial={{ opacity: 0, y: -40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.45, delay: 0.25 }}
                  >
                    <FiShield className={styles.standForIcon} />
                    <span className={styles.standForListText}>
                      Integrity in Every Deal
                    </span>
                  </motion.li>
                </ul>
                <ul className={styles.standForList}>
                  <motion.li
                    initial={{ opacity: 0, y: -40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.45, delay: 0.25 }}
                  >
                    <FiCpu className={styles.standForIcon} />
                    <span className={styles.standForListText}>
                      Driven by Technology
                    </span>
                  </motion.li>
                  <motion.li
                    initial={{ opacity: 0, y: -40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.45, delay: 0.5 }}
                  >
                    <FiSmile className={styles.standForIcon} />
                    <span className={styles.standForListText}>
                      Crafted for a New Generation
                    </span>
                  </motion.li>
                  <motion.li
                    initial={{ opacity: 0, y: -40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.45, delay: 0.5 }}
                  >
                    <FiAward className={styles.standForIcon} />
                    <span className={styles.standForListText}>
                      Uncompromising Standards
                    </span>
                  </motion.li>
                </ul>
              </div>
            </div>
            <div className={styles.standForRight}>
              <motion.div
                className={styles.standForImgWrapper}
                initial={{ opacity: 0, x: 120 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.8,
                  type: "spring",
                  stiffness: 50,
                  delay: 0.2,
                }}
              >
                <img
                  src={require("../../assets/other/16.png")}
                  alt="Profile"
                  className={styles.standForHeroImg}
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <FaqSection
          FAQ_DATA={FAQ_DATA}
          faqOpenIndex={faqOpenIndex}
          handleFaqToggle={handleFaqToggle}
        />
        {/* Contact Prompt Section */}
        <ContactPromptSection text="Have a question we have not answered?" />
        {/* Certifications Section */}
        <CertificationsSection
          logoIGI={logoIGI}
          logoJBT={logoJBT}
          logoVDB={logoVDB}
        />
      </div>
    </>
  );
};

export default Home;
